import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Box, Grid } from '@material-ui/core';
import React from 'react';
import { HeatMapObjective } from '../../../HeatMap/components/HeatMapObjective';
import { ContentContext } from '../../context/content-context';
import {
  DYNAMIC_VIEW_REDUCER_ACTIONS,
  useDynamicViewDispatch,
  useDynamicViewState,
} from '../../context/dynamic-view-context';
import {
  generateFilterPeriod,
  generateFilterPeriodHeatmap,
} from '../../utils/period';
import DpCard from '../card/DpCard';
import { AbsoluteFrecuencyChart } from '../charts/AbsoluteFrecuencyChart';
import { AvailabilityConsolidatedByStepChart } from '../charts/AvailabilityConsolidatedByStepChart';
import { AvailabilityConsolidatedChart } from '../charts/AvailabilityConsolidatedChart';
import { AvailabilityDowntimeChart } from '../charts/AvailabilityDowntimeChart';
import { AvailabilityDowntimeGlobalChart } from '../charts/AvailabilityDowntimeGlobalChart';
import { AvailabilityDowntimeGlobalWithErrorChart } from '../charts/AvailabilityDowntimeGlobalWithError';
import { AvailabilityIsp } from '../charts/AvailabilityIsp';
import { ConsolidatedAvailabilityBySteps } from '../charts/ConsolidatedAvailabilityBySteps';
import ConsolidatedMeasurementsChart from '../charts/ConsolidatedMeasurementsChart';
import { CumulativeFrecuencyChart } from '../charts/CumulativeFrecuencyChart';
import { DistributionConsolidatedChart } from '../charts/DistributionConsolidatedChart';
import EventsMeasurementsChart from '../charts/EventsMeasurementsChart';
import { GaugeChart } from '../charts/GaugeChart';
import { ResponseTimeConsolidatedChart } from '../charts/ResponseTimeConsolidatedChart';
import { ResponseTimeForNodeChart } from '../charts/ResponseTimeForNodeChart';
import { ResponseTimeForStepChart } from '../charts/ResponseTimeForStepChart';
import { SelectNode } from '../custom/SelectNode';
import { SelectStep } from '../custom/SelectStep';

const HEIGHT_UNIT = 20;

/**
 * @fileoverview Componente wrapper para elementos sortables (arrastrables) en el grid.
 * @module SortableItem
 */

/**
 * Componente que representa un widget individual dentro del layout dinámico.
 *
 * Utiliza `ui-dnd-kit/sortable` para habilitar funcionalidades de arrastrar y soltar.
 * Renderiza dinámicamente el componente específico basado en `slot.component` y
 * le proporciona el contexto necesario (dimensiones, filtros, configuración).
 *
 * @param {Object} props - Propiedades del componente.
 * @param {Object} props.slot - Objeto de configuración del slot/widget.
 * @param {string} props.slot.id - Identificador único del slot.
 * @param {number} props.slot.cols - Número de columnas que ocupa el widget (Grid size).
 * @param {string} props.slot.component - Identificador del tipo de componente a renderizar.
 * @param {number} props.slot.objetivoId - ID del objetivo asociado al widget.
 * @param {Object} [props.slot.customProperties] - Propiedades personalizadas del widget.
 *
 * @returns {React.ReactElement} Widget renderizado envuelto en un Grid item arrastrable.
 *
 * @author David Maldonado
 * (c) Servicios de Monitoreo S.A. - Santiago, Chile
 */
export const SortableItem = React.memo(({ slot }) => {
  const {
    activeLayout,
    filterPeriod,
    isEditing,
    isEditingTitle,
    refreshTime,
  } = useDynamicViewState();
  const dispatch = useDynamicViewDispatch();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: slot.id, disabled: !isEditing || isEditingTitle });

  const selectedPeriod = React.useMemo(
    () => generateFilterPeriod(filterPeriod),
    [filterPeriod],
  );
  const selectedPeriodHeatmap = React.useMemo(
    () => generateFilterPeriodHeatmap(filterPeriod),
    [filterPeriod],
  );

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    minHeight: 10 * HEIGHT_UNIT,
    
  };

  /**
   * Manejador para cambiar el tamaño (ancho en columnas) de un widget.
   * Actualiza el layout activo en el estado global.
   *
   * @param {Object} params - Parámetros del cambio de tamaño.
   * @param {string} params.id - ID del widget que se está redimensionando.
   * @param {number} params.size - Nuevo tamaño en columnas (e.g., 3, 4, 6, 12).
   */
  const handleChangeGridSize = React.useCallback(
    ({ id, size }) => {
      const updatedLayout = activeLayout.map((item) => {
        if (item.id === id) {
          return { ...item, cols: size };
        }
        return item;
      });

      dispatch({
        type: DYNAMIC_VIEW_REDUCER_ACTIONS.UPDATE_TEMPLATE,
        payload: updatedLayout,
      });
    },
    [activeLayout, dispatch],
  );

  const contextValue = React.useMemo(() => ({ slot, handleChangeGridSize }), [
    slot,
    handleChangeGridSize,
  ]);

  return (
    <Grid
      item
      lg={slot.cols}
      md={slot.cols}
      sm={12}
      xs={12}
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
    >
      <ContentContext.Provider value={contextValue}>
        <DpCard>
          <DpCard.TitleActions />
          {slot.component === 'id59-m4-s6' ? (
            <Box
              display="flex"
              justifyContent="center"
              padding={2}
              width="100%"
              flexGrow={1}
            >
              <GaugeChart
                objetivoId={slot.objetivoId}
                real={true}
                excluirSinMonitoreo={slot.customParams?.excluirSinMonitoreo}
              />
            </Box>
          ) : slot.component === 'id93-m16-s1' ? (
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              padding={2}
              width="100%"
              flexGrow={1}
            >
              <HeatMapObjective
                objetivoId={slot.objetivoId}
                globalStatePeriod={selectedPeriodHeatmap}
                periodo={selectedPeriod.period}
                showDateHeatmap={true}
                showWeekdayHeatmap={false}
                refreshTime={refreshTime}
              />
            </Box>
          ) : slot.component === 'id61-m4-s6' ? (
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              padding={2}
              width="100%"
              flexGrow={1}
            >
              <AvailabilityConsolidatedChart
                objetivoId={slot.objetivoId}
                periodo={filterPeriod}
              />
            </Box>
          ) : slot.component === 'id100-m4-s6' ? (
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              padding={2}
              width="100%"
              flexGrow={1}
            >
              <AvailabilityIsp objetivoId={slot.objetivoId} />
            </Box>
          ) : slot.component === 'id94-m16-s2' ? (
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              padding={2}
              width="100%"
              flexGrow={1}
            >
              <HeatMapObjective
                objetivoId={slot.objetivoId}
                globalStatePeriod={selectedPeriodHeatmap}
                periodo="d"
                showDateHeatmap={false}
                showWeekdayHeatmap={true}
                refreshTime={refreshTime}
              />
            </Box>
          ) : slot.component === 'id96-m4-s6' ? (
            <>
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                padding={2}
                width="100%"
                flexGrow={1}
              >
                <AvailabilityConsolidatedByStepChart
                  objetivoId={slot.objetivoId}
                  periodo={filterPeriod}
                  nodoId={slot.customProperties?.nodoId || 0}
                />
              </Box>
              <DpCard.Footer>
                <Box width={200} display="flex" justifyContent="flex-end">
                  <SelectNode />
                </Box>
              </DpCard.Footer>
            </>
          ) : slot.component === 'id62-m4-s6' ? (
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              padding={2}
              width="100%"
              flexGrow={1}
            >
              <ResponseTimeConsolidatedChart
                objetivoId={slot.objetivoId}
                periodo={filterPeriod}
              />
            </Box>
          ) : slot.component === 'id97-m4-s6' ? (
            <>
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                padding={2}
                width="100%"
                flexGrow={1}
              >
                <ResponseTimeForStepChart
                  objetivoId={slot.objetivoId}
                  pasoId={slot.customProperties?.pasoId || 0}
                />
              </Box>
              <DpCard.Footer>
                <Box width={200} display="flex" justifyContent="flex-end">
                  <SelectStep />
                </Box>
              </DpCard.Footer>
            </>
          ) : slot.component === 'id98-m4-s6' ? (
            <>
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                padding={2}
                width="100%"
                flexGrow={1}
              >
                <ResponseTimeForNodeChart
                  objetivoId={slot.objetivoId}
                  pasoId={slot.customProperties?.pasoId || 0}
                />
              </Box>
              <DpCard.Footer>
                <Box width={200} display="flex" justifyContent="flex-end">
                  <SelectStep />
                </Box>
              </DpCard.Footer>
            </>
          ) : slot.component === 'id105-m4-s6' ? (
            <Box
              display="flex"
              flexDirection="column"
              padding={2}
              width="100%"
              flexGrow={1}
            >
              <EventsMeasurementsChart objetivoId={slot.objetivoId} />
            </Box>
          ) : slot.component === 'id104-m4-s6' ? (
            <Box
              display="flex"
              flexDirection="column"
              padding={2}
              width="100%"
              flexGrow={1}
            >
              <ConsolidatedMeasurementsChart objetivoId={slot.objetivoId} />
            </Box>
          ) : slot.component === 'id64-m4-s6' ? (
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              padding={2}
              width="100%"
              flexGrow={1}
            >
              <AbsoluteFrecuencyChart objetivoId={slot.objetivoId} />
            </Box>
          ) : slot.component === 'id103-m4-s6' ? (
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              padding={2}
              width="100%"
              flexGrow={1}
            >
              <CumulativeFrecuencyChart objetivoId={slot.objetivoId} />
            </Box>
          ) : slot.component === 'id65-m4-s6' ? (
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              padding={2}
              width="100%"
              flexGrow={1}
            >
              <AvailabilityDowntimeChart objetivoId={slot.objetivoId} />
            </Box>
          ) : slot.component === 'id66-m4-s6' ? (
            <>
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                padding={2}
                width="100%"
                flexGrow={1}
              >
                <AvailabilityDowntimeGlobalChart
                  objetivoId={slot.objetivoId}
                  pasoId={slot.customProperties?.pasoId || 0}
                />
              </Box>
              <DpCard.Footer>
                <Box width={200} display="flex" justifyContent="flex-end">
                  <SelectStep />
                </Box>
              </DpCard.Footer>
            </>
          ) : slot.component === 'id102-m4-s6' ? (
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              padding={2}
              width="100%"
              flexGrow={1}
            >
              <ConsolidatedAvailabilityBySteps objetivoId={slot.objetivoId} />
            </Box>
          ) : slot.component === 'id106-m4-s6' ? (
            <>
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="center"
                padding={2}
                width="100%"
                flexGrow={1}
              >
                <DistributionConsolidatedChart
                  objetivoId={slot.objetivoId}
                  pasoId={slot.customProperties?.pasoId || 0}
                />
              </Box>
              <DpCard.Footer>
                <Box width={200} display="flex" justifyContent="flex-end">
                  <SelectStep />
                </Box>
              </DpCard.Footer>
            </>
          ) : slot.component === 'id107-m4-s6' ? (
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              padding={2}
              width="100%"
              flexGrow={1}
            >
              <AvailabilityDowntimeGlobalWithErrorChart
                objetivoId={slot.objetivoId}
              />
            </Box>
          ) : null}
        </DpCard>
      </ContentContext.Provider>
    </Grid>
  );
});
